import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { MarketId } from 'types/market-id.enum';
import { loadState, saveState } from 'utils/storage';

type State = {
  showSnowfall: boolean;
  toggleSnowfall(): void;
};

type SnowfallContextProviderProps = {
  children: React.ReactNode;
};

const SnowfallContext = React.createContext<State | undefined>(undefined);

const DEFAULT_MARKETS = [MarketId.Norway, MarketId.UnitedKingdom];

const SnowfallContextProvider: React.FC<SnowfallContextProviderProps> = ({ children }) => {
  const {
    account: { company },
  } = useAccountState();

  const [showSnowfall, setShowSnowfall] = useState<boolean>(loadState({ key: 'snowfall' }));

  const toggleSnowfall = useCallback((): void => {
    setShowSnowfall((prev) => {
      saveState({ key: 'snowfall', state: !prev });
      return !prev;
    });
  }, [setShowSnowfall]);

  useEffect(() => {
    if (company) {
      if (dayjs().isBetween(dayjs('2024-12-19').utc(true), dayjs('2025-01-02').utc(true))) {
        // auto enable for selected markets between December 19, 2024 and January 2, 2025

        if (DEFAULT_MARKETS.includes(company.marketId)) {
          // if market is Norway or UK
          const initialSnowfallState = loadState({ key: 'snowfall' });

          if (initialSnowfallState !== null) {
            // key exists in storage, use stored value
            const savedShowSnowfall = loadState({ key: 'snowfall' });
            setShowSnowfall(savedShowSnowfall);
          } else if (initialSnowfallState === null) {
            // key does not exist in storage, default to enabled
            setShowSnowfall(true);
            saveState({ key: 'snowfall', state: true });
          }
        }
      }
    }
  }, [company]);

  return <SnowfallContext.Provider value={{ showSnowfall, toggleSnowfall }}>{children}</SnowfallContext.Provider>;
};

function useSnowfallState() {
  const context = useContext(SnowfallContext);
  if (context === undefined) {
    throw new Error('useSnowfallState must be used within a SnowfallContextProvider');
  }
  return context;
}

export { SnowfallContextProvider, useSnowfallState };
